import { createColumnHelper, useReactTable } from '@tanstack/react-table';
import { useMemo } from 'react';
import { twJoin } from 'tailwind-merge';
import { useAccount } from 'wagmi';
import { OPEN_ORDER_STATUSES, TRADE_PAGE_TABLE_ROWS } from '../../constants';
import { PORTFOLIO_PAGE } from '../../constants/featureFlags';
import { PORTFOLIO_TRADES_ORDER_HISTORY_TAB_URL } from '../../constants/routes';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useFlattenedPages } from '../../hooks/useFlattenedPages';
import { Order, OrderParams, useOrders } from '../../hooks/useOrders';
import { ProductType, useProductsById } from '../../hooks/useProducts';
import { usePropsAsState } from '../../hooks/usePropAsState';
import { useActiveSelectedSubaccount } from '../../hooks/useSelectedSubaccount';
import { useTableSorting } from '../../hooks/useTableSorting';
import { useTailwindBreakpoint } from '../../hooks/useTailwindBreakpoint';
import { formatDate, formatDollar, formatNumber } from '../../lib/formatters';
import { getDecimalPlaces, multiply } from '../../lib/math';
import { getTradeLabel, getTriggerCondition, orderStatusLabels } from '../../lib/orderHelpers';
import { toLowerCase } from '../../lib/stringHelpers';
import { PrettyMerge } from '../../lib/tsUtilityTypes';
import { OrderSide } from '../../types/generated';
import { DateRange } from '../DateRangeInput/DateRangeInput';
import {
  CommonTableProps,
  getCommonTableParams,
  getRowLeftBorderClassName,
} from '../Table/commonTableParams';
import { getMarketColumn } from '../Table/getMarketColumn';
import { StateTable } from '../Table/Table';
import { TwoRowCell } from '../Table/TwoRowCell';

const DEFAULT_SORT = { id: 'time', desc: true } as const;

// Map front-end column IDs to backend orderBy field names
const SORT_FIELD_MAP = {
  type: 'type',
  size_token0: 'quantity',
  price_with_decimals: 'price',
  time: 'createdAt',
} as const satisfies Record<string, OrderParams['orderBy']>;

type TableData = {
  ticker: string;
  displayTicker: string;
  side: Order['side'];
  type: Lowercase<Order['type']>;
  size: bigint;
  filled: bigint;
  price: bigint;
  close: boolean;
  id: string;
  reduceOnly: boolean;
  tradeLabel: string;
  time: Date;
  triggerCondition: string;
  baseTokenName: string;
  status: Order['status'];
  sizeDecimals: number;
  priceDecimals: number;
};

export const OrderHistoryTableUi: React.FC<
  PrettyMerge<
    {
      tableData: TableData[];
      isConnected: boolean;
    },
    CommonTableProps
  >
> = ({
  tableData,

  // Table props
  isConnected,
  isFetching,
  isPending,
  isError,
  pagination,
  viewAllLink,
  pageSize,
  paginationKey,
  sorting,
  setSorting,
  fetchNextPage,
  setPageSize,
  pageSizeOptions,
}) => {
  const isLg = useTailwindBreakpoint('lg');
  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<TableData>();

    return [
      {
        ...getMarketColumn(columnHelper, {
          displaySide: !isLg,
          enableSorting: 'baseTokenName_market_side' in SORT_FIELD_MAP,
        }),
        meta: {
          accordion: {
            showInTrigger: true,
          },
        },
      },
      columnHelper.accessor('type', {
        enableSorting: 'type' in SORT_FIELD_MAP,
        header: 'Order Type',
        meta: { cellContainerClassName: 'capitalize', accordion: { showInTrigger: true } },
      }),
      columnHelper.accessor(({ side, tradeLabel }) => ({ side, tradeLabel }), {
        id: 'trade_label_side',
        enableSorting: 'trade_label_side' in SORT_FIELD_MAP,
        header: 'Trade Type',
        cell: (info) => {
          const { tradeLabel, side } = info.getValue();
          return (
            <span
              className={twJoin(
                'text-nowrap',
                side === OrderSide.BUY ? 'text-contentGreen' : 'text-contentRed'
              )}
            >
              {tradeLabel}
            </span>
          );
        },
      }),
      columnHelper.accessor(
        ({ filled, baseTokenName, sizeDecimals, type, price }) => ({
          filled,
          baseTokenName,
          sizeDecimals,
          type,
          price,
        }),
        {
          id: 'filled_baseTokenName_type',
          enableSorting: 'filled_baseTokenName_type' in SORT_FIELD_MAP,
          header: 'Filled Size',
          cell: (info) => {
            const { filled, baseTokenName, sizeDecimals, type, price } = info.getValue();
            return type === 'market' ? (
              <span className="text-nowrap">
                {formatNumber(filled, {
                  minimumFractionDigits: sizeDecimals,
                  maximumFractionDigits: sizeDecimals,
                })}{' '}
                {baseTokenName}
              </span>
            ) : (
              <TwoRowCell
                primaryVal={`${formatNumber(filled, {
                  minimumFractionDigits: sizeDecimals,
                  maximumFractionDigits: sizeDecimals,
                })} ${baseTokenName}`}
                secondaryVal={formatDollar(multiply(filled, price))}
              />
            );
          },
        }
      ),
      columnHelper.accessor(
        ({ size, baseTokenName, sizeDecimals, close, type, price }) => ({
          size,
          baseTokenName,
          sizeDecimals,
          close,
          type,
          price,
        }),
        {
          id: 'size_token0',
          enableSorting: 'size_token0' in SORT_FIELD_MAP,
          header: 'Total Size',
          meta: {
            accordion: {
              showInTrigger: true,
            },
          },
          cell: (info) => {
            const { size, baseTokenName, sizeDecimals, close, type, price } = info.getValue();
            return (
              <span className="text-nowrap">
                {close ? (
                  'Close Position'
                ) : type === 'market' ? (
                  <span className="text-nowrap">
                    {formatNumber(size, {
                      minimumFractionDigits: sizeDecimals,
                      maximumFractionDigits: sizeDecimals,
                    })}{' '}
                    {baseTokenName}
                  </span>
                ) : (
                  <TwoRowCell
                    primaryVal={`${formatNumber(size, {
                      minimumFractionDigits: sizeDecimals,
                      maximumFractionDigits: sizeDecimals,
                    })} ${baseTokenName}`}
                    secondaryVal={formatDollar(multiply(size, price))}
                  />
                )}
              </span>
            );
          },
        }
      ),
      columnHelper.accessor(({ price, priceDecimals, type }) => ({ price, priceDecimals, type }), {
        id: 'price_decimals_type',
        enableSorting: 'price_with_decimals' in SORT_FIELD_MAP,
        header: 'Order Price',
        cell: (info) => {
          const { price, priceDecimals, type } = info.getValue();
          if (type === 'market') {
            return <span className="text-contentSecondary">Market</span>;
          }
          return formatNumber(price, {
            minimumFractionDigits: priceDecimals,
            maximumFractionDigits: priceDecimals,
          });
        },
      }),
      columnHelper.accessor('reduceOnly', {
        enableSorting: 'reduceOnly' in SORT_FIELD_MAP,
        header: 'Reduce Only',
        cell: (info) => (info.getValue() ? 'Yes' : 'No'),
      }),
      columnHelper.accessor('triggerCondition', {
        enableSorting: 'triggerCondition' in SORT_FIELD_MAP,
        header: 'Trigger Condition',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('time', {
        enableSorting: 'time' in SORT_FIELD_MAP,
        header: 'Time',
        cell: (info) => <span className="text-nowrap">{formatDate(info.getValue())}</span>,
      }),
      columnHelper.accessor('status', {
        enableSorting: 'status' in SORT_FIELD_MAP,
        header: 'Status',
        cell: (info) => {
          const status = info.getValue();
          const isOpenOrder = Boolean(OPEN_ORDER_STATUSES.find((s) => s === status));
          return isOpenOrder ? 'Open' : orderStatusLabels[status];
        },
        meta: {
          align: 'right',
        },
      }),
    ];
  }, [isLg]);

  const table = useReactTable({
    columns,
    data: tableData,
    meta: {
      getRowClassName: (row) => twJoin('lg:h-9', getRowLeftBorderClassName(row)),
    },
    ...getCommonTableParams({
      pageSize,
      sorting,
      setSorting,
    }),
  });

  return (
    <StateTable
      table={table}
      isPending={isPending}
      isFetching={isFetching}
      isError={isError}
      isConnected={isConnected}
      viewAllLink={viewAllLink}
      noDataText="Your orders will appear here."
      showPaginationControls={pagination}
      fetchNextPage={fetchNextPage}
      paginationKey={paginationKey}
      pageSize={pageSize}
      setPageSize={setPageSize}
      pageSizeOptions={pageSizeOptions}
    />
  );
};

export const OrderHistoryTable: React.FC<{
  products?: ProductType[];
  pagination?: boolean;
  side?: OrderSide;
  dateRange?: DateRange;
  defaultPageSize?: number;
  pageSizeOptions?: number[];
}> = ({
  products,
  pagination,
  side,
  dateRange,
  defaultPageSize = TRADE_PAGE_TABLE_ROWS,
  pageSizeOptions = [defaultPageSize],
}) => {
  const { isConnected } = useAccount();
  const { value: portfolioPageEnabled } = useFeatureFlag(PORTFOLIO_PAGE);
  const selectedSubaccount = useActiveSelectedSubaccount();

  const { sorting, setSorting, orderBy, order } = useTableSorting(DEFAULT_SORT, SORT_FIELD_MAP);

  const [pageSize, setPageSize] = usePropsAsState(defaultPageSize);

  // Memoize useOrder params so we can track if pagination needs to be reset
  const useOrderParams = useMemo(
    () =>
      ({
        subaccountId: selectedSubaccount?.id,
        limit: 2 * pageSize,
        productIds: products?.map(({ id }) => id),
        side,
        createdAfter: dateRange?.from?.getTime(),
        createdBefore: dateRange?.to?.getTime(),
        orderBy,
        order,
      }) as const satisfies OrderParams,
    [
      dateRange?.from,
      dateRange?.to,
      pageSize,
      products,
      selectedSubaccount?.id,
      side,
      orderBy,
      order,
    ]
  );

  const {
    data: orderData,
    isPending,
    isFetching,
    isError,
    fetchNextPage,
    isFetchingNextPage,
  } = useOrders(useOrderParams);

  const productsById = useProductsById();

  const orders = useFlattenedPages('orders', orderData?.pages);

  const tableData = useMemo(() => {
    if (!orders.length) return [];

    return orders.map((order) => {
      const product = productsById?.[order.productId];
      const priceDecimals = getDecimalPlaces(product?.tickSize || 0n);
      return {
        id: order.id,
        ticker: product?.ticker || '',
        displayTicker: product?.displayTicker || '',
        type: toLowerCase(order.type),
        side: order.side,
        size: order.quantity,
        filled: order.filled,
        price: order.price,
        close: order.close,
        baseTokenName: product?.baseTokenName || '',
        reduceOnly: order.reduceOnly,
        tradeLabel: getTradeLabel(order.side, order.reduceOnly),
        time: new Date(order.createdAt),
        triggerCondition: getTriggerCondition(order, priceDecimals),
        status: order.status,
        sizeDecimals: getDecimalPlaces(product?.lotSize || 0n),
        priceDecimals,
      };
    });
  }, [orders, productsById]);

  return (
    <OrderHistoryTableUi
      tableData={tableData}
      isConnected={isConnected}
      isFetching={isFetching || isFetchingNextPage}
      isPending={isPending || !productsById}
      isError={isError}
      pagination={pagination}
      setSorting={setSorting}
      sorting={sorting}
      pageSize={pageSize}
      setPageSize={setPageSize}
      pageSizeOptions={pagination ? pageSizeOptions : [pageSize]}
      fetchNextPage={fetchNextPage}
      paginationKey={useOrderParams}
      viewAllLink={
        portfolioPageEnabled && !pagination ? PORTFOLIO_TRADES_ORDER_HISTORY_TAB_URL : undefined
      }
    />
  );
};
